import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../../../util/reactIntl';
import ReviewCard from '../../../../components/ReviewCard/ReviewCard';
import ReviewsSubTabNavigation from '../ReviewsSubTabNavigation/ReviewsSubTabNavigation';
import css from './ReviewsGivenView.module.css';

/**
 * ReviewsGivenView component displays reviews where store is customer
 */
const ReviewsGivenView = ({
  reviews,
  achievementReviews,
  totalReviews,
  inProgress,
  error,
  intl,
  onLoadMore,
  pagination,
  // Navigation props
  activeSubTab,
  totalReviewsReceived,
  totalReviewsGiven,
  onSubTabChange,
}) => {
  if (inProgress && reviews.length === 0) {
    return (
      <div className={css.container}>
        <div className={css.reviewsLayout}>
        <div className={css.loadingState}>
          <FormattedMessage
            id="StoreProfilePage.reviews.loading"
            defaultMessage="Loading reviews..."
          />
        </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.container}>
        <div className={css.errorState}>
          <FormattedMessage
            id="StoreProfilePage.reviews.error"
            defaultMessage="Error loading reviews. Please try again."
          />
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className={css.container}>
        <div className={css.reviewsLayout}>
          <div className={css.reviewsMain}>

            {/* Sub-tab Navigation */}
            <ReviewsSubTabNavigation
              activeSubTab={activeSubTab}
              totalReviewsReceived={totalReviewsReceived}
              totalReviewsGiven={totalReviewsGiven}
              onSubTabChange={onSubTabChange}
            />
            <div className={css.emptyState}>
              <FormattedMessage
                id="StoreProfilePage.reviews.empty.given"
                defaultMessage="No reviews given yet."
              />
            </div>
          </div>

        </div>
      </div>
    );
  }

  const hasMore = pagination && pagination.page < pagination.totalPages;
  const observerTarget = useRef(null);

  // Sort reviews by newest first
  const sortedReviews = [...reviews].sort((a, b) => {
    const dateA = new Date(a.attributes?.createdAt || 0);
    const dateB = new Date(b.attributes?.createdAt || 0);
    return dateB - dateA; // Newest first
  });

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !inProgress) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, inProgress, onLoadMore]);

  return (
    <div className={css.container}>
      <div className={css.reviewsLayout}>
      <div className={css.reviewsMain}>
        {/* Sub-tab Navigation */}
        <ReviewsSubTabNavigation
          activeSubTab={activeSubTab}
          totalReviewsReceived={totalReviewsReceived}
          totalReviewsGiven={totalReviewsGiven}
          onSubTabChange={onSubTabChange}
        />

        <div className={css.reviewsHeader}>
          <h3 className={css.reviewsTitle}>
            <FormattedMessage
              id="StoreProfilePage.tabs.reviews.given.title"
              defaultMessage="Reviews Given"
            />
          </h3>
          <p className={css.reviewsSubtitle}>
            <FormattedMessage
              id="StoreProfilePage.reviews.given.subtitle"
              defaultMessage="Reviews you've written for other stores."
            />
          </p>
        </div>

        <div className={css.reviewsList}>
          {sortedReviews.map((review, index) => (
            <ReviewCard
              key={review.id?.uuid || review.id || index}
              review={review}
              intl={intl}
              showRentalContext={true}
            />
          ))}
        </div>

        {/* Infinite scroll trigger */}
        {hasMore && (
          <div ref={observerTarget} className={css.infiniteScrollTrigger}>
            {inProgress && (
              <div className={css.loadingIndicator}>
                <FormattedMessage
                  id="StoreProfilePage.reviews.loading"
                  defaultMessage="Loading..."
                />
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

ReviewsGivenView.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalReviews: PropTypes.number.isRequired,
  inProgress: PropTypes.bool,
  error: PropTypes.object,
  intl: intlShape.isRequired,
  onLoadMore: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    perPage: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  // Navigation props
  activeSubTab: PropTypes.string.isRequired,
  totalReviewsReceived: PropTypes.number,
  totalReviewsGiven: PropTypes.number,
  onSubTabChange: PropTypes.func.isRequired,
};

export default injectIntl(ReviewsGivenView);

